import React, { useMemo, useState } from "react";
import { FiltersButton, InfoBanner, Spinner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SupplementsForDishesFilters from "./SupplementsForDishesFilters";
import SupplementsForDishesTable from "./SupplementsForDishesTable";
import { DishForTree, PartialDishSupplement, PartialMenuItem } from "../../../interfaces/meMenuInterfaces/interfaces";
import SupplementsGroupModal, {
    SupplementsGroupFormValues,
    SupplementsGroupSubmitValues,
} from "./modals/SupplementsGroupModal/SupplementsGroupModal";
import {
    useCreateMenuSupplementMutation,
    useGetMenuItemsForSupplementsQuery,
    useRestoreMenuSupplementMutation,
    useUpdateMenuSupplementMutation,
} from "@me-team/host/main/dishSupplements/graphql/dishSupplements.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import i18next from "i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { mapDishesFromBackendToTreeDishes } from "./utils/dishesInputServices/mapDishesFromBackendToTreeDishes";
import { buildSupplementInput } from "./utils/buildSupplementInput";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { useCloseTooltipsOnModalOpen } from "../../../hooks/useCloseTooltipsOnModalOpen";
import { FormikHelpers } from "formik";
import { getGraphqlErrorCategory } from "./utils/helpers";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import MenuLanguageVersionSelectContainer from "../components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildPrimaryLanguageVm, buildSelectedLanguageVm } from "./utils/languageVm";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export interface AddOnsFilter {
    search: string;
}

export type SupplementsGroupModalMode = "create" | "edit" | "restore";

export interface SupplementsGroupModalState {
    isOpen: boolean;
    mode: SupplementsGroupModalMode;
    group: PartialDishSupplement | null;
}

const initialValueFilter: AddOnsFilter = {
    search: "",
};

const SupplementsForDishesPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams<{ branchId: string }>();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState<AddOnsFilter>(initialValueFilter);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [modalState, setModalState] = useState<SupplementsGroupModalState>({
        isOpen: false,
        mode: "create",
        group: null,
    });
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();

    const [updateMenuSupplement] = useUpdateMenuSupplementMutation();
    const [createMenuSupplement] = useCreateMenuSupplementMutation();
    const [restoreMenuSupplement] = useRestoreMenuSupplementMutation();

    const [listAddOnsRefetch, setListAddOnsRefetch] = useState<(() => Promise<unknown>) | null>(null);
    const canCreateOptionsAndSupplements = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsCreate);
    const canEditOptionsAndSupplements = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsEdit);
    const canDeleteRestoreOptionsAndSupplements = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsDeleteRestore);

    const toggleFilters = () => setShowFilters((prev) => !prev);

    const {
        loading: menuLanguagesLoading,
        primaryLanguage,
        languages,
    } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );
    const modalLocale =
        modalState.mode === "create" ? (primaryLanguageVm?.code ?? null) : (selectedLangVersionCode ?? null);

    const { data: dishesFromCategoriesData, loading: dishesFromCategoriesLoading } = useGetMenuItemsForSupplementsQuery(
        {
            variables: {
                companyId: Number(currentCompanyId),
                itemsPerPage: 100000,
                isDeleted: false,
                locale: modalLocale,
            },
            skip: !currentCompanyId || !modalState.isOpen || !modalLocale,
            context: {
                errorType: "local",
            },
            fetchPolicy: "network-only",
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        }
    );

    const dishesFromCategories: PartialMenuItem[] = dishesFromCategoriesData?.user?.company?.menuItems?.items;

    const dishesData: DishForTree[] = useMemo(
        () => mapDishesFromBackendToTreeDishes(dishesFromCategories),
        [dishesFromCategories]
    );

    const openCreateModal = () => {
        if (!canCreateOptionsAndSupplements) return;

        const primaryCode = primaryLanguageVm?.code;
        if (primaryCode) {
            menuLanguageVersionVar(primaryCode);
        }
        setModalState({
            isOpen: true,
            mode: "create",
            group: null,
        });
    };

    const openEditModal = (group: PartialDishSupplement) => {
        if (!canEditOptionsAndSupplements) return;

        setModalState({
            isOpen: true,
            mode: "edit",
            group,
        });
    };

    const openRestoreModal = (group: PartialDishSupplement) => {
        if (!canDeleteRestoreOptionsAndSupplements) return;

        setModalState({
            isOpen: true,
            mode: "restore",
            group,
        });
    };

    const handleCloseModal = () => {
        resetTooltips();
        setModalState((prev) => ({
            ...prev,
            isOpen: false,
        }));
    };

    const handleSubmitModal = async (
        values: SupplementsGroupSubmitValues,
        formikHelpers: FormikHelpers<SupplementsGroupFormValues>
    ) => {
        if (!currentCompanyId) return;

        const companyId = Number(currentCompanyId);
        const currentGroup = modalState.group;

        formikHelpers.setFieldError("groupName", undefined);

        try {
            if (modalState.mode === "create") {
                if (!canCreateOptionsAndSupplements) return;

                const input = buildSupplementInput(values);

                await createMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        input,
                        locale: primaryLanguageVm?.code,
                    },
                    context: { errorType: "local" },
                });

                if (typeof listAddOnsRefetch === "function") {
                    await listAddOnsRefetch();
                }
                toggleShowToast(t("Created"));
            }

            if (modalState.mode === "edit" && currentGroup) {
                if (!canEditOptionsAndSupplements) return;

                const input = buildSupplementInput(values);

                await updateMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        dishId: currentGroup.id,
                        input,
                        locale: selectedLangVersionCode,
                    },
                    context: { errorType: "local" },
                });
                if (typeof listAddOnsRefetch === "function") {
                    await listAddOnsRefetch();
                }
                toggleShowToast(t("Saved"));
            }

            if (modalState.mode === "restore" && currentGroup) {
                if (!canDeleteRestoreOptionsAndSupplements) return;

                await restoreMenuSupplement({
                    variables: {
                        branchId: Number(branchId),
                        id: currentGroup.id,
                    },
                    context: { errorType: "local" },
                });
                if (typeof listAddOnsRefetch === "function") {
                    await listAddOnsRefetch();
                }
                toggleShowToast(t("Restored"));
            }

            handleCloseModal();
        } catch (error) {
            const apolloError = error as ApolloError;
            const errorCategory = getGraphqlErrorCategory(error);
            if (errorCategory === "menu_supplement.already_exists") {
                formikHelpers.setFieldError("groupName", t("A group with this name and options already exists"));
                return;
            }
            setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
        }
    };

    const isModalLoading =
        modalState.isOpen &&
        (menuLanguagesLoading || dishesFromCategoriesLoading || !primaryLanguageVm || !selectedLanguageVm);

    return (
        <>
            <div className="mb-4 px-2 px-lg-0">
                <InfoBanner>
                    <p className="fs-7 mb-0">
                        {t(
                            "On this page, you can create and edit options and additions to dishes (for example, additives, sauces, or cooking options), as well as set their cost and availability."
                        )}
                    </p>
                </InfoBanner>
                <TableHeader
                    title={t("Options and Add-ons for dishes")}
                    paths={[
                        { path: `/branches/${branchId}`, label: t("Branches") },
                        { path: "", label: t("Options and Add-ons for dishes") },
                    ]}
                    buttons={
                        <Row className="g-3 justify-content-end mt-3 mt-lg-0">
                            <Col xs={12} sm={5} xxl={4} style={{ minWidth: "226px" }}>
                                <div className="position-relative">
                                    <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                        {t("Language version")}
                                    </span>
                                    <MenuLanguageVersionSelectContainer companyId={Number(currentCompanyId)} />
                                </div>
                            </Col>
                            <WithRoleAccess permission={CompanyAccess.MenuOptionsAndSupplementsCreate}>
                                <Col xs={9} sm={5} xxl={4}>
                                    <Button
                                        variant="primary"
                                        className="fw-normal w-100 rounded-1 fw-bold text-truncate text-capitalize"
                                        onClick={openCreateModal}
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>
                                        {t("Create Group")}
                                    </Button>
                                </Col>
                            </WithRoleAccess>
                            <Col xs={3} sm={2} xxl={3}>
                                <FiltersButton
                                    filter={filters}
                                    showFilters={showFilters}
                                    onClick={toggleFilters}
                                    classesText="d-none d-xxl-inline-block"
                                />
                            </Col>
                        </Row>
                    }
                    showFilters={showFilters}
                >
                    <SupplementsForDishesFilters setFilters={setFilters} />
                </TableHeader>
                <SupplementsForDishesTable
                    versionLocale={selectedLangVersionCode}
                    filters={filters}
                    onCreateGroup={openCreateModal}
                    onEditGroup={openEditModal}
                    onRestoreGroup={openRestoreModal}
                    onRefetchReady={setListAddOnsRefetch}
                    tooltipResetKey={tooltipResetKey}
                    resetTooltips={resetTooltips}
                    canCreateOptionsAndSupplements={canCreateOptionsAndSupplements}
                    canEditOptionsAndSupplements={canEditOptionsAndSupplements}
                    canDeleteRestoreOptionsAndSupplements={canDeleteRestoreOptionsAndSupplements}
                />
            </div>
            {modalState.isOpen ? (
                isModalLoading ? (
                    <Spinner />
                ) : (
                    dishesData &&
                    primaryLanguageVm &&
                    selectedLanguageVm && (
                        <SupplementsGroupModal
                            show={modalState.isOpen}
                            handleClose={handleCloseModal}
                            onSave={handleSubmitModal}
                            dishesData={dishesData}
                            existingGroup={modalState.group || undefined}
                            mode={modalState.mode}
                            primaryLanguage={primaryLanguageVm}
                            selectedLanguage={selectedLanguageVm}
                            canCreateOptionsAndSupplements={canCreateOptionsAndSupplements}
                            canEditOptionsAndSupplements={canEditOptionsAndSupplements}
                            canDeleteRestoreOptionsAndSupplements={canDeleteRestoreOptionsAndSupplements}
                        />
                    )
                )
            ) : null}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default SupplementsForDishesPage;
